import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { taskService, uploadService } from '../services/api';

// Esquema de validación
const TaskSchema = Yup.object().shape({
  title: Yup.string()
    .required('El título es obligatorio')
    .min(5, 'El título debe tener al menos 5 caracteres')
    .max(100, 'El título no puede exceder los 100 caracteres'),
  description: Yup.string()
    .required('La descripción es obligatoria')
    .min(20, 'La descripción debe tener al menos 20 caracteres'),
  category: Yup.string()
    .required('La categoría es obligatoria'),
  location: Yup.string()
    .required('La ubicación es obligatoria'),
  tokens: Yup.number()
    .required('El pago en tokens es obligatorio')
    .positive('Los tokens deben ser un número positivo')
    .integer('Los tokens deben ser un número entero')
});

const CreateTask = () => {
  const navigate = useNavigate();
  const [apiError, setApiError] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  
  const categories = [
    'Diseño',
    'Programación',
    'Marketing',
    'Traducción',
    'Administrativa',
    'Otra'
  ];
  
  const handleImageChange = (event, setFieldValue) => {
    const files = Array.from(event.currentTarget.files);
    
    // Validar que no sean más de 3 imágenes
    if (files.length + previewImages.length > 3) {
      setApiError('No puedes subir más de 3 imágenes por tarea');
      return;
    }
    
    // Crear previsualizaciones
    const newPreviewImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setPreviewImages([...previewImages, ...newPreviewImages]);
  };
  
  const removeImage = (index) => {
    const newPreviewImages = [...previewImages];
    // Liberar URL de objeto para evitar memory leaks
    URL.revokeObjectURL(newPreviewImages[index].preview);
    newPreviewImages.splice(index, 1);
    setPreviewImages(newPreviewImages);
    
    const newUploadedUrls = [...uploadedImageUrls];
    newUploadedUrls.splice(index, 1);
    setUploadedImageUrls(newUploadedUrls);
  };
  
  const uploadImages = async () => {
    if (previewImages.length === 0) return [];
    
    setUploadingImages(true);
    
    try {
      const uploadPromises = previewImages.map(async (image) => {
        // Solo subir si no tiene ya una URL (si es nuevo)
        if (!uploadedImageUrls.includes(image.preview)) {
          const response = await uploadService.uploadImage(image.file);
          return response.data.imageUrl;
        }
        return image.preview; // Si ya está subido, mantener la URL
      });
      
      const imageUrls = await Promise.all(uploadPromises);
      setUploadedImageUrls(imageUrls);
      return imageUrls;
    } catch (error) {
      console.error('Error al subir imágenes:', error);
      setApiError('Error al subir las imágenes. Por favor, intenta de nuevo.');
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };
  
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Subir imágenes primero
      let imageUrls = [];
      if (previewImages.length > 0) {
        try {
          imageUrls = await uploadImages();
        } catch (error) {
          setSubmitting(false);
          return; // Terminar si falla la subida de imágenes
        }
      }
      
      // Crear la tarea con las URLs de las imágenes
      const taskData = {
        ...values,
        images: imageUrls
      };
      
      const response = await taskService.createTask(taskData);
      
      // Redirigir a la página de detalles de la tarea creada
      navigate(`/tasks/${response.data._id}`);
    } catch (error) {
      setApiError(error.response?.data?.message || 'Error al crear la tarea');
      setSubmitting(false);
    }
  };
  
  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h2 className="h4 mb-0">Crear Nueva Tarea</h2>
            </div>
            <div className="card-body">
              {apiError && (
                <div className="alert alert-danger" role="alert">
                  {apiError}
                </div>
              )}
              
              <Formik
                initialValues={{
                  title: '',
                  description: '',
                  category: '',
                  location: '',
                  tokens: 10
                }}
                validationSchema={TaskSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, errors, touched, setFieldValue }) => (
                  <Form>
                    <div className="mb-3">
                      <label htmlFor="title" className="form-label">Título</label>
                      <Field
                        type="text"
                        id="title"
                        name="title"
                        className={`form-control ${errors.title && touched.title ? 'is-invalid' : ''}`}
                        placeholder="Título descriptivo de la tarea"
                      />
                      <ErrorMessage name="title" component="div" className="invalid-feedback" />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="description" className="form-label">Descripción</label>
                      <Field
                        as="textarea"
                        id="description"
                        name="description"
                        rows="4"
                        className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`}
                        placeholder="Describe detalladamente la tarea a realizar"
                      />
                      <ErrorMessage name="description" component="div" className="invalid-feedback" />
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label htmlFor="category" className="form-label">Categoría</label>
                        <Field
                          as="select"
                          id="category"
                          name="category"
                          className={`form-select ${errors.category && touched.category ? 'is-invalid' : ''}`}
                        >
                          <option value="">Selecciona una categoría</option>
                          {categories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="category" component="div" className="invalid-feedback" />
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <label htmlFor="location" className="form-label">Ubicación</label>
                        <Field
                          type="text"
                          id="location"
                          name="location"
                          className={`form-control ${errors.location && touched.location ? 'is-invalid' : ''}`}
                          placeholder="Ciudad, país o remoto"
                        />
                        <ErrorMessage name="location" component="div" className="invalid-feedback" />
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="tokens" className="form-label">Pago en BLUE Tokens</label>
                      <Field
                        type="number"
                        id="tokens"
                        name="tokens"
                        min="1"
                        className={`form-control ${errors.tokens && touched.tokens ? 'is-invalid' : ''}`}
                      />
                      <ErrorMessage name="tokens" component="div" className="invalid-feedback" />
                      <div className="form-text">
                        Los tokens BLUE son la moneda de pago en la plataforma.
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="images" className="form-label">
                        Imágenes (opcional, máx. 3)
                      </label>
                      <input
                        type="file"
                        id="images"
                        name="images"
                        className="form-control"
                        onChange={(e) => handleImageChange(e, setFieldValue)}
                        accept="image/*"
                        multiple
                        disabled={previewImages.length >= 3}
                      />
                      <div className="form-text">
                        Puedes subir hasta 3 imágenes para ilustrar la tarea.
                      </div>
                    </div>
                    
                    {previewImages.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex flex-wrap gap-2">
                          {previewImages.map((image, index) => (
                            <div key={index} className="position-relative">
                              <img
                                src={image.preview}
                                alt={`Preview ${index + 1}`}
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                className="rounded"
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                onClick={() => removeImage(index)}
                              >
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="d-grid mt-4">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={isSubmitting || uploadingImages}
                      >
                        {isSubmitting || uploadingImages ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {uploadingImages ? 'Subiendo imágenes...' : 'Creando tarea...'}
                          </>
                        ) : 'Crear Tarea'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTask;
